import React, { useState, useEffect } from "react";
import { Facebook, Instagram, X, Sparkles } from "lucide-react";
import { SOCIAL } from "../../lib/social";

/**
 * FollowUsBar — slim, dismissible top bar nudging visitors to follow
 * CampCompass on Facebook + Instagram. Designed to live ABOVE every
 * other promo bar so it's the first thing campers see.
 *
 *   • Hides itself for 7 days when dismissed (per-device localStorage).
 *   • Self-hides on print / share routes so screen-recorded reels and
 *     printable posters stay clean.
 *   • Links open in new tabs with rel="noopener" — never blocks the
 *     reader from the page they came to read.
 */
const DISMISS_KEY = "cc_follow_bar_dismissed_until";
const SUPPRESS_PATHS = ["/print/", "/share/", "/admin", "/launch-pack"];

export default function FollowUsBar() {
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Don't render on routes used for screenshots / admin tools.
    const path = window.location.pathname || "";
    if (SUPPRESS_PATHS.some((p) => path.startsWith(p))) return;
    // Respect per-device 7-day dismissal.
    try {
      const until = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
      if (until > Date.now()) return;
    } catch { /* localStorage may be blocked */ }
    setHidden(false);
  }, []);

  if (hidden) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(
        DISMISS_KEY,
        String(Date.now() + 7 * 24 * 60 * 60 * 1000),
      );
    } catch { /* ignore */ }
    setHidden(true);
  };

  return (
    <div
      className="bg-[#0e0e0e] text-white border-b border-[#262626]"
      data-testid="follow-us-bar"
      role="region"
      aria-label="Follow CampCompass UK on social"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-2 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-3.5 h-3.5 text-[#fde047] shrink-0" aria-hidden="true" />
          <span className="text-xs sm:text-[13px] leading-snug truncate">
            <span className="hidden sm:inline">Follow us for big updates · new campsites · honest UK camping tips</span>
            <span className="sm:hidden">Follow us — updates &amp; daily drops</span>
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a
            href={SOCIAL.facebook.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#1877f2] hover:bg-[#0c63d4] transition-colors text-white text-xs font-bold mono uppercase tracking-[0.1em]"
            data-testid="follow-us-fb"
            aria-label="Follow CampCompass on Facebook"
          >
            <Facebook className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Facebook</span>
            <span className="sm:hidden">FB</span>
          </a>
          <a
            href={SOCIAL.instagram.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-white text-xs font-bold mono uppercase tracking-[0.1em]"
            style={{ background: "linear-gradient(135deg,#fa7e1e 0%,#d62976 50%,#962fbf 100%)" }}
            data-testid="follow-us-ig"
            aria-label="Follow CampCompass on Instagram"
          >
            <Instagram className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Instagram</span>
            <span className="sm:hidden">IG</span>
          </a>
          <button
            type="button"
            onClick={dismiss}
            className="p-1 hover:bg-white/10 text-[#bbb] hover:text-white transition-colors"
            data-testid="follow-us-dismiss"
            aria-label="Dismiss follow us bar"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
